import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import adminApi from '../api/client';
import { useAuth } from './AuthContext';

export interface ActionQueue {
  key: string; label: string; path: string; nav_path: string;
  count: number | null; status: 'available' | 'unavailable' | 'disabled';
}
interface Summary { queues: ActionQueue[]; generated_at: string }
interface ActionsState {
  summary: Summary | null; loading: boolean; error: boolean; refresh: () => void;
}
const ActionsContext = createContext<ActionsState | undefined>(undefined);

export const AdminActionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const refreshRef = useRef<() => void>(() => {});
  const refresh = useCallback(() => refreshRef.current(), []);

  useEffect(() => {
    setSummary(null); setError(false); setLoading(false);
    if (!isAuthenticated) return;
    let active = true;
    let running = false;
    let refreshAfterRequest = false;
    let controller: AbortController | undefined;
    let mutationTimer: ReturnType<typeof setTimeout> | undefined;
    const fetchSummary = async () => {
      if (!active || document.visibilityState === 'hidden') return;
      if (running) { refreshAfterRequest = true; return; }
      running = true;
      controller = new AbortController();
      setLoading(true);
      try {
        const result = await adminApi.get<Summary>('/admin/action-summary', { signal: controller.signal });
        const data = result.data;
        if (!Array.isArray(data.queues) || typeof data.generated_at !== 'string' || data.queues.some(queue =>
          !['available', 'unavailable', 'disabled'].includes(queue.status) ||
          (queue.status === 'available' && (!Number.isSafeInteger(queue.count) || (queue.count ?? -1) < 0)))) {
          throw new Error('Invalid action summary');
        }
        if (active) { setSummary(data); setError(false); }
      } catch {
        if (active) setError(true);
      } finally {
        running = false;
        if (active) {
          setLoading(false);
          if (refreshAfterRequest) { refreshAfterRequest = false; void fetchSummary(); }
        }
      }
    };
    refreshRef.current = () => { void fetchSummary(); };
    const onMutation = () => {
      clearTimeout(mutationTimer);
      mutationTimer = setTimeout(() => { void fetchSummary(); }, 500);
    };
    const onVisible = () => { if (document.visibilityState !== 'hidden') void fetchSummary(); };
    void fetchSummary();
    const interval = setInterval(() => { void fetchSummary(); }, 60_000);
    window.addEventListener('admin-actions-changed', onMutation);
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false; controller?.abort(); clearInterval(interval); clearTimeout(mutationTimer);
      refreshRef.current = () => {};
      window.removeEventListener('admin-actions-changed', onMutation);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated]);

  return <ActionsContext.Provider value={{ summary, loading, error, refresh }}>{children}</ActionsContext.Provider>;
};

export function useAdminActions(): ActionsState {
  const context = useContext(ActionsContext);
  if (!context) throw new Error('AdminActionsProvider is required');
  return context;
}

export function queueCount(queues: ActionQueue[]): { count: number; unavailable: boolean } {
  return {
    count: queues.reduce((total, queue) => total + (queue.status === 'available' ? queue.count ?? 0 : 0), 0),
    unavailable: queues.some(queue => queue.status === 'unavailable'),
  };
}
